/**
 * Auth Store — in-memory access token storage.
 *
 * Replaces localStorage for the JWT access token so XSS cannot read it.
 * The httpOnly refresh cookie remains the persistence mechanism across
 * browser sessions; on app boot we silently mint a new access token from it.
 *
 * Usage:
 *   // From non-React code (api.js, tokenRefresh.js, hooks):
 *   import { getToken, setToken, clearToken } from '../stores/authStore';
 *   const t = getToken();
 *
 *   // From React components that need to re-render on token changes:
 *   import { useAuthToken } from '../stores/authStore';
 *   const token = useAuthToken();
 */

import { useEffect, useState } from 'react';

let accessToken = null;
const listeners = new Set();

// Set while an intentional logout is in progress so the api.js 401 interceptors
// don't hijack the redirect with "?expired=true" when a trailing/in-flight
// request 401s against the just-revoked session. Logout owns the navigation.
let loggingOut = false;
export function beginLogout() { loggingOut = true; }
export function endLogout() { loggingOut = false; }
export function isLoggingOut() { return loggingOut; }

function notify() {
  listeners.forEach((l) => {
    try { l(accessToken); } catch (e) { console.error('authStore listener error:', e); }
  });
}

export function getToken() {
  return accessToken;
}

export function setToken(token) {
  accessToken = token || null;
  notify();
}

export function clearToken() {
  accessToken = null;
  notify();
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAuthToken() {
  const [token, setLocalToken] = useState(accessToken);
  useEffect(() => subscribe(setLocalToken), []);
  return token;
}
